import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';
import {
  formatBrandMoney,
  formatBrandMoneyCompact,
  getBrandCurrencyOptions,
  getBrandCurrencySuffix,
  getBrandDefaultCurrency,
  getBrandLocale,
  getBrandVatRate,
  amountExclVat,
  vatFromTtc,
  SAUDI_CURRENCY_SYMBOL,
  SAUDI_CURRENCY_CODE,
} from '../../../shared/utils/brandCurrency';

export {
  formatBrandMoney,
  formatBrandMoneyCompact,
  getBrandCurrencyOptions,
  getBrandDefaultCurrency,
  getBrandVatRate,
  amountExclVat,
  vatFromTtc,
  SAUDI_CURRENCY_SYMBOL,
  SAUDI_CURRENCY_CODE,
};

export function getComptabiliteDateLocale(language) {
  return getBrandLocale(language);
}

export function formatComptabiliteDate(value, language, options = {}) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const locale = getComptabiliteDateLocale(language);
  if (options.monthYear) {
    return d.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }
  if (options.exportTimestamp) {
    return d.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });
}

export function formatComptabiliteMoney(amount) {
  return formatBrandMoney(amount);
}

export function formatComptabiliteMoneyCompact(amount) {
  return formatBrandMoneyCompact(amount);
}

/** Chart/table suffix — never "DA" on Saudi brand. */
export function getComptabiliteCurrencySuffix() {
  if (isSaudiBrand()) return SAUDI_CURRENCY_SYMBOL;
  return getBrandCurrencySuffix();
}

export function formatComptabiliteChartAxis(value, language) {
  if (value == null || Number.isNaN(Number(value))) return value;
  const formatted = new Intl.NumberFormat(getComptabiliteDateLocale(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value));
  return `${formatted} ${getComptabiliteCurrencySuffix()}`;
}

export function buildComptabiliteChartOptions(language) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'rgba(255, 255, 255, 0.7)',
        bodyColor: 'rgba(255, 255, 255, 0.7)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const val = context.parsed?.y ?? context.parsed ?? context.raw;
            const label = context.dataset.label || '';
            return label ? `${label}: ${formatBrandMoney(val)}` : formatBrandMoney(val);
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(255, 255, 255, 0.1)' },
        ticks: {
          color: 'rgba(255, 255, 255, 0.7)',
          callback: (value) => formatComptabiliteChartAxis(value, language),
        },
      },
      x: {
        grid: { display: false },
        ticks: { color: 'rgba(255, 255, 255, 0.7)' },
      },
    },
  };
}

export function buildComptabiliteDoughnutOptions() {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          boxWidth: 12,
          padding: 15,
          color: 'rgba(255, 255, 255, 0.7)',
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'rgba(255, 255, 255, 0.7)',
        bodyColor: 'rgba(255, 255, 255, 0.7)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const val = context.parsed ?? context.raw;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total ? ((val / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${formatBrandMoney(val)} (${pct}%)`;
          },
        },
      },
    },
    cutout: '60%',
  };
}

/** SOCPA/ZATCA (Saudi) vs PCN (legacy Algeria). */
export function getComplianceFrameworkKey() {
  return isSaudiBrand() ? 'conformite_socpa' : 'conformite_pcn';
}

/** Income statement tax line label key. */
export function getIncomeTaxLineKey() {
  return isSaudiBrand() ? 'tax_zakat_corporate' : 'tax_ibs';
}

export function getVatRateLabel() {
  return `${getBrandVatRate()}%`;
}

export function getExportCurrencyHeader() {
  if (isSaudiBrand()) {
    return `${SAUDI_CURRENCY_CODE} (${SAUDI_CURRENCY_SYMBOL})`;
  }
  return `${getBrandDefaultCurrency()} (${getBrandCurrencySuffix()})`;
}

export const COMPTA_CHART_SOCPA = 'SOCPA';
export const COMPTA_CHART_IFRS = 'IFRS';
export const COMPTA_CHART_LEGACY_NSCF = 'NSCF';
export const COMPTA_CHART_LEGACY_PCN = 'PCN';

export function getDefaultChartStandard() {
  return isSaudiBrand() ? COMPTA_CHART_SOCPA : COMPTA_CHART_LEGACY_NSCF;
}

export function getChartStandardOptions() {
  if (isSaudiBrand()) {
    return [
      { id: COMPTA_CHART_SOCPA, label: 'SOCPA' },
      { id: COMPTA_CHART_IFRS, label: 'IFRS' },
    ];
  }
  return [
    { id: COMPTA_CHART_LEGACY_NSCF, label: 'NSCF' },
    { id: COMPTA_CHART_LEGACY_PCN, label: 'PCN' },
  ];
}

export function buildChartStandardMap(chart) {
  if (isSaudiBrand()) {
    return { [COMPTA_CHART_SOCPA]: chart, [COMPTA_CHART_IFRS]: chart };
  }
  return { [COMPTA_CHART_LEGACY_NSCF]: chart, [COMPTA_CHART_LEGACY_PCN]: chart };
}

export function getBrandDatePickerLocale() {
  return isSaudiBrand() ? 'ar' : 'fr';
}

export function getComptabiliteDatePickerLocale() {
  return getBrandDatePickerLocale();
}

export function formatComptabiliteDateTime(value, language) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(getComptabiliteDateLocale(language), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatComptabiliteNumber(value, language, options = {}) {
  const n = typeof value === 'number' ? value : parseFloat(value) || 0;
  return n.toLocaleString(getComptabiliteDateLocale(language), {
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  });
}

export const SAUDI_VAT_ACCOUNT_PREFIXES = ['2204', '1190', '220401', '119001'];
export const LEGACY_VAT_ACCOUNT_PREFIXES = ['445', '4456', '4457'];

export function getVatAccountPrefixes() {
  return isSaudiBrand() ? SAUDI_VAT_ACCOUNT_PREFIXES : LEGACY_VAT_ACCOUNT_PREFIXES;
}

export function getDefaultLocationPlaceholder() {
  return isSaudiBrand() ? 'الرياض، المملكة العربية السعودية' : 'الجزائر العاصمة، الجزائر';
}

export function getAccountClassDefinitions() {
  if (isSaudiBrand()) {
    return {
      '1': { name: 'Assets', color: '#3b82f6' },
      '2': { name: 'Liabilities', color: '#10b981' },
      '3': { name: 'Equity', color: '#f59e0b' },
      '4': { name: 'Revenue', color: '#059669' },
      '5': { name: 'Cost of Sales', color: '#ef4444' },
      '6': { name: 'Operating Expenses', color: '#dc2626' },
      '7': { name: 'Other Income & Expenses', color: '#8b5cf6' },
    };
  }
  return {
    '1': { name: 'حسابات رأس المال', color: '#3b82f6' },
    '2': { name: 'حسابات الأصول الثابتة', color: '#10b981' },
    '3': { name: 'حسابات المخزون', color: '#f59e0b' },
    '4': { name: 'حسابات الأطراف', color: '#ef4444' },
    '5': { name: 'حسابات مالية', color: '#8b5cf6' },
    '6': { name: 'حسابات المصاريف', color: '#dc2626' },
    '7': { name: 'حسابات الإيرادات', color: '#059669' },
  };
}

export function getDemoBalanceAccounts() {
  if (isSaudiBrand()) {
    return [
      { compte: '120101 - Accounts Receivable', debit: 250000, credit: 180000, solde: 70000 },
      { compte: '210101 - Accounts Payable', debit: 120000, credit: 180000, solde: -60000 },
      { compte: '110101 - Cash & Bank', debit: 500000, credit: 350000, solde: 150000 },
      { compte: '410101 - Sales Revenue', debit: 0, credit: 850000, solde: -850000 },
      { compte: '510101 - Cost of Goods Sold', debit: 480000, credit: 0, solde: 480000 },
      { compte: '610101 - Salaries & Wages', debit: 120000, credit: 0, solde: 120000 },
      { compte: '220401 - VAT Payable (ZATCA)', debit: 80000, credit: 120000, solde: -40000 },
      { compte: '230101 - Long-term Loans', debit: 50000, credit: 200000, solde: -150000 },
      { compte: '150101 - Buildings', debit: 600000, credit: 0, solde: 600000 },
      { compte: '150201 - Accumulated Depreciation', debit: 0, credit: 120000, solde: -120000 },
    ];
  }
  return [
    { compte: '411 - العملاء', debit: 2500000, credit: 1800000, solde: 700000 },
    { compte: '401 - الموردون', debit: 1200000, credit: 1800000, solde: -600000 },
    { compte: '512 - البنك', debit: 5000000, credit: 3500000, solde: 1500000 },
    { compte: '701 - المبيعات', debit: 0, credit: 8500000, solde: -8500000 },
    { compte: '601 - المشتريات', debit: 4800000, credit: 0, solde: 4800000 },
    { compte: '641 - الرواتب', debit: 1200000, credit: 0, solde: 1200000 },
    { compte: '445 - ضريبة القيمة المضافة', debit: 800000, credit: 1200000, solde: -400000 },
    { compte: '164 - القروض', debit: 500000, credit: 2000000, solde: -1500000 },
    { compte: '213 - المباني', debit: 6000000, credit: 0, solde: 6000000 },
    { compte: '281 - الاستهلاكات', debit: 0, credit: 1200000, solde: -1200000 },
  ];
}

export function getDemoActivityAmounts() {
  if (isSaudiBrand()) {
    return [25000, -8500, -12000, -120000, 45000];
  }
  return [250000, -85000, -120000, -1200000, 450000];
}
